/**
 * Canonical schema.org entity graph for Hirebee.
 *
 * WHY THIS EXISTS
 * Before this file, ~31 Organization nodes were inlined across 25 page files as anonymous
 * `{ "@type": "Organization", name: "Hirebee" }` objects — under five different names
 * (Hirebee / Hirebee HRMS / Hirebee HRIS / Hirebee ESS / Hirebee HRMS Team). To an entity
 * extractor those read as unrelated blobs, not one company. Everything here hangs off two
 * stable `@id`s so the site describes ONE resolvable entity; per-page schema should reference
 * it via `orgRef` rather than inlining another copy.
 *
 * SOURCING RULE
 * Every value below is taken verbatim from somewhere already in this repo (footer, our-story
 * JSON-LD, trust badges, legal pages). `legalName` comes from the Terms, Privacy and Refund
 * pages ("DeepRedMedia LLC (DBA Hirebee)"). Do not add fields we cannot source —
 * `numberOfEmployees`, `vatID` and `foundingLocation` are deliberately absent because no value
 * for them exists anywhere in the codebase, and inventing them is a factual claim about a real
 * company.
 */

import { decodeHtmlEntitiesSSR, stripHtmlTags, stripInvisibleChars } from "@/lib/utils";

export const SITE_URL = (
  process.env.NEXT_PUBLIC_SITE_URL || "https://hirebee.ai"
).replace(/\/+$/, "");

/** Stable entity anchors. The `#organization` convention already had in-repo precedent. */
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const LOGO_ID = `${SITE_URL}/#logo`;
/** The HR glossary as one entity, so /hr-terms and /hr-terms/[slug] resolve to the same set. */
export const GLOSSARY_SET_ID = `${SITE_URL}/hr-terms#glossary`;

/** Reference the org by @id instead of inlining an anonymous duplicate. */
export const orgRef = { "@id": ORG_ID } as const;
export const websiteRef = { "@id": WEBSITE_ID } as const;

/**
 * The only real, servable raster logo in the repo (1800x680).
 * NOT favicon.ico (.ico is not an accepted format for Google's logo requirement) and NOT
 * /og-image.jpg or /icons/app-icon-512.png — both of those are 404s that were being emitted
 * by the old inline nodes.
 */
export const logoNode = {
  "@type": "ImageObject",
  "@id": LOGO_ID,
  url: `${SITE_URL}/hirebee-logo-light.png`,
  contentUrl: `${SITE_URL}/hirebee-logo-light.png`,
  width: 1800,
  height: 680,
  caption: "Hirebee",
};

export const organizationNode = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Hirebee",
  legalName: "DeepRedMedia LLC",
  // The name variants that appear across the site. Declaring them here is what lets an
  // extractor collapse them onto one entity instead of guessing.
  alternateName: ["Hirebee HRMS", "Hirebee HRIS", "Hirebee ESS", "Hirebee.ai"],
  url: `${SITE_URL}/`,
  logo: logoNode,
  image: { "@id": LOGO_ID },
  description: "The fastest growing global hiring solution",
  foundingDate: "2020",
  founder: [
    { "@type": "Person", name: "Vahe Avetisyan" },
    { "@type": "Person", name: "Luiza Avetisyan" },
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "75 5th St NW",
    addressLocality: "Atlanta",
    addressRegion: "GA",
    postalCode: "30308",
    addressCountry: "US",
  },
  // E.164. The old inline node had "+1(470) 809-9036", which is not a valid phone format.
  telephone: "+14708099036",
  email: "hello@hirebee.ai",
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "hello@hirebee.ai",
      telephone: "+14708099036",
      areaServed: "US",
      availableLanguage: ["en"],
    },
  ],
  // Third-party profiles for this same entity. The review-site ones carry disproportionate
  // weight — they are what answer engines consult for "best ATS" style questions, and sameAs
  // is how we claim them.
  sameAs: [
    "https://www.linkedin.com/company/hirebee-ai/",
    "https://www.facebook.com/HirebeeInc/",
    "https://www.instagram.com/hirebee.ai/",
    "https://www.youtube.com/@hirebeeai8174",
    "https://www.g2.com/products/hirebee-ai/reviews",
    "https://www.capterra.com/p/201026/Hirebee/reviews/",
    "https://www.trustradius.com/products/hirebee-ai/reviews",
    "https://www.getapp.com/hr-employee-management-software/a/hirebee/",
  ],
};

export const websiteNode = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: `${SITE_URL}/`,
  name: "Hirebee",
  description: "The fastest growing global hiring solution",
  publisher: orgRef,
  inLanguage: "en",
  // No SearchAction / sitelinks searchbox: Google deprecated that rich result in Nov 2024 and
  // ignores it. Add one only if /blog ever gets a real server-rendered ?q= search route.
};

/**
 * Wrap nodes in a single @graph so they cross-reference by @id in one script tag.
 * Falsy entries are dropped, so callers can pass `cond && node` inline.
 */
export function graph(...nodes: unknown[]) {
  return {
    "@context": "https://schema.org",
    "@graph": nodes.filter(Boolean),
  };
}

/* ------------------------------------------------------------------ FAQs -- */

export type Faq = { question: string; answer: string };

/**
 * Plain text for JSON-LD string fields. Used for FAQ answers (features store them as HTML) and
 * for titles/headlines -- WordPress-imported post titles routinely carry `&#8217;` and `&amp;`,
 * which must not reach structured data raw.
 *
 * Order matters: close block tags into spaces FIRST (a bare tag strip turns
 * `<p>A</p><p>B</p>` into `AB`), then strip, then decode entities, then collapse
 * whitespace. Decoding before stripping would let an encoded `&lt;p&gt;` become a real
 * tag and get eaten.
 */
export function plainText(raw: string): string {
  // Invisible characters are stripped here as well as in the body-HTML chain, because the
  // columns this runs over are NOT `content` and so never pass through it. `faq_schema` on
  // /blog/how-to-conduct-hr-compliance-risk-assessment-efficiently carried U+034F inside its
  // answers ("integral<CGJ> to the <CGJ><CGJ>smooth operation"), which reached both the FAQ
  // rich-result structured data and the rendered FAQ section. normalizeFaqs() and every
  // breadcrumb name funnel through here, so one call covers all of them.
  return stripInvisibleChars(
    decodeHtmlEntitiesSSR(
      stripHtmlTags(
        String(raw ?? "").replace(/<\/(p|div|li|ul|ol|h[1-6]|tr|td)>|<br\s*\/?>/gi, " "),
      ),
    ),
  )
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Collapse every FAQ shape in this codebase into one list. There are four live shapes
 * plus an alternate answer key, and pages mix them:
 *
 *   recruitment_features.faq_schema                     [{question, answer}]         (HTML answers)
 *   recruitment_features.structured_data.faqs           [{question, answer}]         (mixed HTML/plain)
 *   recruitment_features.structured_data.fixed_sections.faqs
 *                                                       {enabled, content:{items:[]}}
 *   blog_posts.faq_schema                               {mainEntity:[{name, acceptedAnswer:{text}}]}
 *   (extract-feature-faqs also tolerates an `answerHtml` key)
 *
 * The validity filter deliberately mirrors UnifiedFAQSection's, so the markup can never
 * claim more Q&A than the page actually renders.
 */
export function normalizeFaqs(input: unknown): Faq[] {
  if (!input) return [];
  const raw = input as Record<string, unknown>;

  const list: unknown[] = Array.isArray(input)
    ? input
    : Array.isArray(raw.mainEntity)
      ? (raw.mainEntity as unknown[])
      : Array.isArray(raw.faqs)
        ? (raw.faqs as unknown[])
        : Array.isArray((raw.content as Record<string, unknown> | undefined)?.items)
          ? ((raw.content as Record<string, unknown>).items as unknown[])
          : [];

  return list
    .map((item) => {
      const f = (item ?? {}) as Record<string, any>;
      return {
        question: plainText(f.question ?? f.name ?? ""),
        answer: plainText(f.answer ?? f.answerHtml ?? f.acceptedAnswer?.text ?? ""),
      };
    })
    .filter((f) => f.question.length > 3 && f.answer.length > 0);
}

/** FAQPage node, or null when there is nothing to claim. */
export function faqPageNode(faqs: Faq[], pageUrl?: string) {
  if (!faqs.length) return null;
  return {
    "@type": "FAQPage",
    ...(pageUrl ? { "@id": `${pageUrl}#faq` } : {}),
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

/* ------------------------------------------------------------------ blog -- */

type AuthorRow = Record<string, any> | null | undefined;

/**
 * Not every `blog_authors` row is a person. "Hirebee.ai team" is the byline on 39 published
 * posts, and schema.org `Person` asserts a human being -- so emitting one there is a factual
 * error, and it advertises a `/blog/author/...` profile URL for an entity that has no profile.
 * Those resolve to the canonical Organization instead.
 *
 * Matches on the NAME only, never `company_position`: a real person whose job title contains
 * "team" (say, "Talent Team Lead") must stay a Person. Checked against all 11 current rows --
 * only "Hirebee.ai team" matches, and none of the seven real-person names do.
 */
function isOrganizationByline(name: string): boolean {
  const n = name.trim().toLowerCase();
  return /\b(team|staff|editors?|editorial|contributors?)\b/.test(n) || /^hirebee(\.ai)?$/.test(n);
}

/**
 * Real Person node for a post author, falling back to the Organization when the post has no
 * author row. The named author with a jobTitle, bio and profile URL is the attribution signal
 * answer engines actually read -- the previous hardcoded `Organization "Hirebee"` threw it away
 * even though the author was already fetched and shown on the page.
 */
export function authorNode(author: AuthorRow) {
  if (!author?.name) return orgRef;

  // A byline, not a human -- see isOrganizationByline. Guarded after the !name check above,
  // so the name is known to be a non-empty string here.
  if (isOrganizationByline(author.name as string)) return orgRef;

  // social_links is a jsonb object ({linkedin, twitter, ...}); keep only real URLs.
  const sameAs = Object.values((author.social_links ?? {}) as Record<string, unknown>)
    .filter((v): v is string => typeof v === "string" && /^https?:\/\//.test(v));

  return {
    "@type": "Person",
    ...(author.slug ? { "@id": `${SITE_URL}/blog/author/${author.slug}#person` } : {}),
    name: author.name as string,
    ...(author.slug ? { url: `${SITE_URL}/blog/author/${author.slug}` } : {}),
    ...(author.company_position ? { jobTitle: author.company_position as string } : {}),
    ...(author.bio ? { description: author.bio as string } : {}),
    ...(author.avatar_url ? { image: author.avatar_url as string } : {}),
    ...(sameAs.length ? { sameAs } : {}),
    ...(Array.isArray(author.expertise_areas) && author.expertise_areas.length
      ? { knowsAbout: author.expertise_areas }
      : {}),
    worksFor: orgRef,
  };
}

/**
 * BlogPosting node shared by both post templates (/blog/[slug] and /blog/[silo]/[postSlug]),
 * which previously carried byte-identical copies of the same broken object.
 *
 * FAQs are deliberately NOT folded in here: they used to ride as `mainEntity: Question[]` on the
 * BlogPosting, which is invalid nesting and not read as an FAQ signal. Callers pass the separate
 * faqPageNode() into graph() instead.
 */
export function blogPostingNode({
  post,
  author,
  url,
}: {
  post: Record<string, any>;
  author?: AuthorRow;
  url: string;
}) {
  const words = post.content
    ? stripHtmlTags(String(post.content)).split(/\s+/).filter(Boolean).length
    : 0;

  return {
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    // WordPress-imported titles/excerpts carry entities (&#8217;, &amp;) and occasional markup.
    headline: plainText(post.title),
    description: plainText(post.meta_description || post.excerpt || "") || undefined,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    datePublished: post.published_at ?? undefined,
    // Freshness is a heavy factor in retrieval-augmented answers, and updated_at already exists.
    dateModified: post.updated_at ?? post.published_at ?? undefined,
    image: post.featured_image_url ?? undefined,
    author: authorNode(author),
    publisher: orgRef,
    isPartOf: { "@type": "Blog", name: "Hirebee HR Blog", url: `${SITE_URL}/blog` },
    inLanguage: "en",
    ...(post.parent_silo_slug
      ? {
          articleSection: String(post.parent_silo_slug).replace(/-/g, " "),
          // articleSection says where the post sits; `about` says what it is ABOUT, which is
          // what an extractor matches a question against. Same treatment as the help articles.
          about: [
            { "@type": "Thing", name: String(post.parent_silo_slug).replace(/-/g, " ") },
            orgRef,
          ],
        }
      : {}),
    ...(words ? { wordCount: words } : {}),
    ...(post.read_time ? { timeRequired: `PT${post.read_time}M` } : {}),
  };
}

/**
 * Strip the site-name suffix from a title so it can be used as a `headline`.
 *
 * `seoTitle` / `meta_title` values end in "| Hirebee HRMS" (or similar) because they are written
 * for the browser tab and the SERP. Google truncates `headline` around 110 characters, so
 * carrying the suffix into structured data spends that budget restating the publisher -- which
 * `publisher: orgRef` already says, unambiguously and by @id.
 *
 * Only strips a trailing "| ...Hirebee..." segment; a pipe used mid-title is left alone.
 */
export function cleanHeadline(title: string): string {
  return plainText(title).replace(/\s*\|\s*[^|]*Hirebee[^|]*$/i, "").trim();
}

/**
 * Does this heading read as a question, i.e. is the content below it an answer?
 *
 * Used to decide which help articles also deserve FAQPage markup. Requires a literal "?" --
 * an interrogative opener alone is not enough, because plenty of procedural titles start that
 * way without asking anything ("How to import your employee data"). Measured against the live
 * data: all 45 question-shaped help titles carry a "?", so the stricter rule loses nothing and
 * cannot mis-classify a how-to guide as a Q&A pair.
 */
export function isQuestionTitle(title: string): boolean {
  return title.trim().endsWith("?");
}

/**
 * Article node for a "Hirebee vs <competitor>" page.
 *
 * The point of `about` is entity resolution: without it nothing in the markup states that the
 * page is about two specific pieces of software, so an extractor has to infer the subjects from
 * the title string. Naming both -- Hirebee by `@id` reference, the competitor as a
 * SoftwareApplication -- is what makes the comparison machine-readable, which is the whole job
 * of these pages for AI answer engines.
 *
 * `headline` is passed separately from the SEO title on purpose: seoTitle carries a
 * "| Hirebee HRMS" suffix that wastes Google's ~110-char headline budget.
 */
export function comparisonArticleNode({
  headline,
  description,
  competitorName,
  pageUrl,
}: {
  headline: string;
  description: string;
  competitorName: string;
  pageUrl: string;
}) {
  return {
    "@type": "Article",
    "@id": `${pageUrl}#article`,
    headline: plainText(headline),
    description: plainText(description),
    inLanguage: "en",
    author: orgRef,
    publisher: orgRef,
    about: [
      orgRef,
      {
        "@type": "SoftwareApplication",
        name: competitorName,
        applicationCategory: "BusinessApplication",
      },
    ],
    mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
  };
}

/**
 * ItemList for a hub/listing page.
 *
 * Why it matters: a listing page's whole job is to answer "what X does Hirebee have?" -- but
 * without an ItemList the members exist only as anchor tags, so an extractor has to guess which
 * links are the list and which are navigation. Enumerating them makes the answer directly
 * quotable.
 *
 * Only pass items the page VISIBLY lists, in the order it lists them, per the standing rule
 * against marking up content that is not rendered. Returns null when empty so `graph()` drops it.
 */
export function itemListNode(
  items: Array<{ name: string; url: string }>,
  opts?: { name?: string; id?: string },
) {
  if (!items.length) return null;
  return {
    "@type": "ItemList",
    ...(opts?.id ? { "@id": opts.id } : {}),
    ...(opts?.name ? { name: opts.name } : {}),
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: plainText(it.name),
      url: it.url,
    })),
  };
}

/** BreadcrumbList from [name, url] pairs. Names are entity-decoded (they are often post titles). */
export function breadcrumbNode(trail: Array<{ name: string; url: string }>) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: plainText(t.name),
      item: t.url,
    })),
  };
}
